import fetcher from 'modules/fetcher'
import moment from 'moment'
// eslint-disable-next-line no-restricted-imports -- redux-form validator runs outside React render
import { t } from 'i18n/utils'
import { toBool, isNestedResourceFieldId, parseNestedResourceFieldId } from '../helpers'
import { shouldPerformEmailVerification, isEmailValidationModeSoft } from 'EmailValidation/utils'
import { callEmailVerificationEndpoint, handleEmailVerificationError } from 'EmailValidation/api'

const initialValidationResult = { valid: true, errorMsg: null, meta: {} }

export const hasNoValue = (fieldValue) => fieldValue === undefined || fieldValue === null || fieldValue === '' || (Array.isArray(fieldValue) && !fieldValue.length)

export const presenceValidator = ({ formField, fieldValue, fieldMeta, getFieldValue }) => {
  const { validatePresenceOn, disabled } = fieldMeta
  const value = validatePresenceOn ? getFieldValue(formField.resourceType, validatePresenceOn) : fieldValue

  const valid = disabled || !hasNoValue(value)

  return {
    valid,
    errorMsg: valid ? null : t('common.validation.required'),
    meta: {}
  }
}

export const emailValidator = ({ fieldValue }) => {
  const valid = hasNoValue(fieldValue) ||
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/i.test(String(fieldValue))

  return {
    valid,
    errorMsg: valid ? null : t('common.validation.notValidEmail'),
    meta: {}
  }
}

export const emailExternalVerificationValidator = async ({ fieldValue }) => {
  if (isEmailValidationModeSoft() || !shouldPerformEmailVerification(fieldValue)) {
    return initialValidationResult
  }

  try {
    const resp = await callEmailVerificationEndpoint(fieldValue)
    return {
      valid: resp.valid,
      errorMsg: resp.error,
      meta: {}
    }
  } catch (error) {
    handleEmailVerificationError(error)
    return initialValidationResult
  }
}

export const currencyValidator = ({ fieldValue }) => {
  const valid = hasNoValue(fieldValue) ||
    /^-?\.?(?:\d+|\d{1,3}(?:[\s.,]\d{3})+)(?:[.,]\d+)?$/.test(String(fieldValue))

  return {
    valid,
    errorMsg: valid ? null : t('common.validation.notValidNumber'),
    meta: {}
  }
}

export const companyNameUniquenessValidator = async ({ fieldValue, resourceId }) => {
  if (hasNoValue(fieldValue)) {
    return initialValidationResult
  }

  const resp = await fetcher('/api/v3/companies.json', {
    query: { view: 'api_v3', name: fieldValue, conditions: { company_name_eq: fieldValue } }
  }).then((resp) => {
    const valid = !resp.entries.length || (resp.entries.length === 1 && resp.entries[0].id === resourceId)

    return {
      valid,
      errorMsg: valid ? null : t('common.validation.mustBeUnique'),
      meta: { duplicates: resp.entries }
    }
  })

  return resp
}

export const eventStartTimeValidator = ({ formField, getFieldValue }) => {
  // Handle nested resources where formField.id contains index prefix (e.g., "0.end_time_date")
  let startTimeDateFieldId = 'start_time_date'
  let startTimeTimeFieldId = 'start_time_time'
  let endTimeDateFieldId = 'end_time_date'
  let endTimeTimeFieldId = 'end_time_time'
  let allDayFieldId = 'all_day'

  if (isNestedResourceFieldId(formField.id)) {
    const { index } = parseNestedResourceFieldId(formField.id)
    startTimeDateFieldId = `${index}.start_time_date`
    startTimeTimeFieldId = `${index}.start_time_time`
    endTimeDateFieldId = `${index}.end_time_date`
    endTimeTimeFieldId = `${index}.end_time_time`
    allDayFieldId = `${index}.all_day`
  }

  const startTimeDate = getFieldValue(formField.resourceType, startTimeDateFieldId)
  const startTimeTime = getFieldValue(formField.resourceType, startTimeTimeFieldId)

  const endTimeDate = getFieldValue(formField.resourceType, endTimeDateFieldId)
  const endTimeTime = getFieldValue(formField.resourceType, endTimeTimeFieldId)

  const allDay = toBool(getFieldValue(formField.resourceType, allDayFieldId))
  const format = allDay ? 'YYYY/MM/DD' : 'YYYY/MM/DD h:mmA'

  const valid = (!startTimeDate || !endTimeDate) || (moment(`${endTimeDate} ${endTimeTime}`, format) >= moment(`${startTimeDate} ${startTimeTime}`, format))

  return {
    valid,
    errorMsg: valid ? null : t('common.validation.shouldBeAfterStartTime'),
    meta: {}
  }
}

export const endDateAfterStartDateValidator = ({ formField, getFieldValue }) => {
  // Handle nested resources where formField.id contains index prefix (e.g., "0.end_date")
  let startDateFieldId = 'start_date'
  let endDateFieldId = 'end_date'

  if (isNestedResourceFieldId(formField.id)) {
    const { index } = parseNestedResourceFieldId(formField.id)
    startDateFieldId = `${index}.start_date`
    endDateFieldId = `${index}.end_date`
  }

  const startDate = getFieldValue(formField.resourceType, startDateFieldId)
  const endDate = getFieldValue(formField.resourceType, endDateFieldId)

  const valid = (!startDate || !endDate) || (moment(endDate) >= moment(startDate))

  return {
    valid,
    errorMsg: valid ? null : t('common.validation.mustBeAfterStartDate'),
    meta: {}
  }
}
